import { CuratedProductListParametersType } from "@sellar-io/api-v2/types";
import { useEffect, useEffectEvent } from "react";
import { Navigate, useNavigate, useMatch, useSearchParams } from "react-router";
import { SpinnerLegacy } from "../../components";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { DISCOVER, SPOTLIGHT } from "../../constants/routes";
import { useEvents } from "../../hooks";
import { useMarketStorefrontSessionId } from "../../hooks/useMarketStorefrontSessionTracking";
import { SpotlightProductList } from "./components/SpotlightProductList";
import { SpotlightSupplierList } from "./components/SpotlightSupplierList";
import { SpotlightProductBySupplierList } from "./components/SpotlightProductBySupplierList";
import { useGetSpotlightPageData } from "./Spotlight.shared";

export type SpotlightPagesType = {
  title: string;
  description?: string;
  logoUrl?: string;
  bannerSmallUrl?: string;
  bannerMediumUrl?: string;
  bannerLargeUrl?: string;
  spotlightBannerUrl?: string;
  query?: CuratedProductListParametersType["query"];
  search?: string;
  hiddenFilters?: ("abv" | "price")[];
};

// TODO: Remove fallback title and description after 03/01/2026

export const Spotlight = () => {
  const { track } = useEvents();
  const { sessionId } = useMarketStorefrontSessionId();
  const navigate = useNavigate();
  const pageId = useMatch(SPOTLIGHT.pattern)?.params.spotlightId;
  const [searchParams] = useSearchParams();
  const spotlightType = searchParams.get("type");
  const productId = searchParams.get("productId");

  const { data: spotlightPage, loading: spotlightPageDataLoading } =
    useGetSpotlightPageData(pageId ?? "");

  const trackPageView = useEffectEvent(() => {
    if (spotlightType && pageId) {
      track("Spotlight Page Viewed", {
        type: spotlightType,
        url: pageId,
        sessionId,
      });
    }
  });

  useEffect(() => {
    trackPageView();
  }, [spotlightType, pageId]);

  if (spotlightPageDataLoading) return <SpinnerLegacy />;

  if (!spotlightPage || !pageId) return <Navigate to="/" />;

  if (
    spotlightPage.type === "queryProductList" &&
    !spotlightPage.query?.length &&
    !spotlightPage.search
  )
    return <Navigate to="/" />;

  const showBanner =
    !!spotlightPage.bannerLargeUrl &&
    !!spotlightPage.bannerMediumUrl &&
    !!spotlightPage.bannerSmallUrl;

  const showHeader = spotlightPage.description && spotlightPage.logoUrl;

  return (
    <>
      <div className="@container">
        <NavigationBar
          leftIcon={"CHEVRON_LEFT"}
          onClickLeftIcon={() => {
            navigate(DISCOVER);
          }}
          title={spotlightPage.title}
        />
        {showHeader && (
          <div className="w-full border-b border-zinc-300 bg-white p-4 py-6">
            <div className="m-auto flex w-full max-w-[1200px] flex-col-reverse items-center gap-4 @4xl:flex-row">
              <div className="flex w-full flex-col gap-2 @4xl:w-1/2">
                {!!spotlightPage.logoUrl && (
                  <img
                    src={spotlightPage.logoUrl}
                    alt={spotlightPage.title}
                    className="hidden w-60 flex-none @3xl:ml-0 @4xl:block"
                  />
                )}
                {!!spotlightPage.description && (
                  <p>{spotlightPage.description}</p>
                )}
              </div>
              {showBanner && (
                <picture className="@4xl:h-[275px] @4xl:w-1/2">
                  <source
                    srcSet={spotlightPage.bannerLargeUrl}
                    media="(min-width: 1400px)"
                  />
                  <source
                    srcSet={spotlightPage.bannerMediumUrl}
                    media="(min-width: 968px)"
                  />
                  <img
                    src={spotlightPage.bannerSmallUrl}
                    alt={spotlightPage.title}
                    className="h-full w-full self-center rounded-lg object-cover"
                  />
                </picture>
              )}
            </div>
          </div>
        )}

        <div className="w-full">
          <div className="m-auto grid w-full max-w-[1200px] flex-col gap-4">
            {spotlightPage.type === "productList" && (
              <SpotlightProductList
                ids={spotlightPage.entityIds}
                showAwardBadge={spotlightPage.pageId === "siba-award-2026"}
              />
            )}
            {spotlightPage.type === "queryProductList" && (
              <SpotlightProductList
                query={spotlightPage.query}
                search={spotlightPage.search}
                ids={productId ? [parseInt(productId)] : undefined}
              />
            )}
            {spotlightPage.type === "supplierList" && (
              <SpotlightSupplierList ids={spotlightPage.entityIds} />
            )}
            {spotlightPage.type === "productBySupplier" && (
              <SpotlightProductBySupplierList ids={spotlightPage.entityIds} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
